/**
 * @fileoverview Actions optimized to update or add messages in the inbox.
 * Provides synchronous and asynchronous actions for efficient management of message state.
 * @module actions/inbox/messagesOptimizedActions
 * @category Actions Layer
 * @since 2.0.0
 * @version 2.1.0
 */

/**
 * Type of action to update or add a message in the store.
 * @constant
 * @type {string}
 * @default
 * @since 2.0.0
 */
export const UPDATE_OR_ADD_MESSAGE = 'UPDATE_OR_ADD_MESSAGE';

/**
 * Synchronous action to update or add a message in the store.
 *
 * @function updateOrAddMessageSync
 * @category Action - Síncrona
 * @memberof module:actions/inbox/messagesOptimizedActions
 *
 * @param {Object} message - Message object to process.
 * @returns {Object} Acción redux con tipo y payload enriquecido.
 *
 * @example
 * dispatch(updateOrAddMessageSync({ id: 456, text: 'Hello', conversationId: 123 }));
 */
export function updateOrAddMessageSync(message, insertIfMissing = true) {
  return {
    type: UPDATE_OR_ADD_MESSAGE,
    payload: {
      message,
      processedByService: true,
      insertIfMissing
    }
  };
}

/**
 * Asynchronous action optimized to update or add a message.
 *
 * Implements the Actions Layer pattern for efficient management of messages in the inbox.
 * Uses services to validate and enrich the message before dispatching the synchronous action.
 *
 * @function updateOrAddMessageOptimized
 * @category Action - Asíncrona
 * @memberof module:actions/inbox/messagesOptimizedActions
 *
 * @param {Object} message - Message object to process.
 * @returns {Function} Thunk function that dispatches the action and returns the result of the operation.
 * @throws {Error} If the message validation fails, returns error in the result object.
 *
 * @example
 * dispatch(updateOrAddMessageOptimized({ id: 456, text: 'Hello', conversationId: 123 }));
 *
 * @since 2.0.0
 * @version 2.1.0 - Integration of validation and enrichment by service
 */
export const updateOrAddMessageOptimized = (message) => async (dispatch, getState) => {
  const state = getState();
  const conversationId = state.conversationsReducer.selectedConversationId;

  const MessageUpdateService = (await import('../../services/inbox/MessageUpdateService')).default;

  // Validation
  const validation = MessageUpdateService.validateMessagePayload(conversationId, message);

  if (!validation.isValid) {
    return { success: false, error: validation.error };
  }

  // Enrichment (without traversing array)
  const conversationState = state.conversationMessagesReducer;

  const enrichedMessage = MessageUpdateService.enrichMessage(
    message,
    conversationState
  );

const insertIfMissing = MessageUpdateService.evaluateInsert(enrichedMessage, conversationState);

  dispatch(updateOrAddMessageSync(enrichedMessage, insertIfMissing));

  return { success: true, message: enrichedMessage };
};
